/**
 * lists.js — the Today board's lists, and which list each item name lives in.
 *
 * LISTS = the built-in ones (config/lists.js BUILTIN_LISTS) merged with what staff changed in
 * the app (LISTS_COLLECTION): lists they created, built-ins they renamed / recoloured /
 * moved, custom lists they deleted (kept, flagged, so History can still label them).
 *
 * ROUTING — an item name lives in ONE list. A note is stored with the list it was made
 * in (`category`); `route(name, fromId)` says where that name belongs NOW:
 *   1. a move someone made ("<fromId>::<name>" → list in the routes doc), if that list
 *      still exists;
 *   2. a list that claims names from `fromId` by rule — Chef Groceries takes the chef's
 *      pantry (dals, flours, spices…) noted under Groceries (logic/categorize.js CLAIM_RULES);
 *   3. otherwise the list it was noted in.
 * Suggestions ("usually needed", ✨ Add my usual), new adds and the Closing tab all follow
 * it — so moving "Besan" once moves every past note's suggestion with it, while History
 * keeps recording the list each note was actually made in.
 *
 * REMOVING — a name can also be taken out of a list's suggestions ("<listId>::<name>" →
 * when, in the routes doc's `hidden` map): notes made before then stop counting there
 * (logic/catalogs.js). Nothing is deleted; noting the name again brings it back.
 */
import { BUILTIN_LISTS } from '../config/lists';
import { ROUTES_DOC_ID } from '../config/constants';
import { CLAIM_RULES } from './categorize';
import { normalizeName } from './names';

const BUILTIN_IDS = new Set(BUILTIN_LISTS.map(c => c.id));
export const isBuiltinList = id => BUILTIN_IDS.has(id);
export const FALLBACK_COLOR = '#94a3b8';

/** Stand-in for an id no list knows (deleted outright, or still loading). */
export const unknownList = id => ({
  id, label: '', short: '', icon: '📋', color: FALLBACK_COLOR, unknown: true, seeds: [], order: 1e6,
});

/** Routes-doc key for "notes of `name` made in list `fromId`". */
export const routeKey = (fromId, name) => `${fromId}::${normalizeName(name)}`;

const byOrder = (a, b) => (a.order - b.order) || ((b.builtin ? 1 : 0) - (a.builtin ? 1 : 0)) || a.label.localeCompare(b.label);

/**
 * Built-ins + the LISTS_COLLECTION docs → every list, in board order.
 * @returns {{ all, active, byId: Map<id, list>, routes: object, hidden: object }}
 *   all    — every list incl. deleted custom ones (History still labels their items)
 *   active — the lists on the board
 *   hidden — names removed from a list's suggestions: { "<listId>::<name>": Timestamp|null }
 */
export function mergeLists(docs = []) {
  let routes = {};
  let hidden = {};
  const overrides = new Map();
  const customDocs = [];
  for (const d of docs || []) {
    if (!d || !d.id) continue;
    if (d.id === ROUTES_DOC_ID) { routes = d.routes || {}; hidden = d.hidden || {}; }
    else if (BUILTIN_IDS.has(d.id)) overrides.set(d.id, d);
    else if (d.custom) customDocs.push(d);
  }
  const builtins = BUILTIN_LISTS.map((c, i) => {
    const o = overrides.get(c.id) || {};
    const label = String(o.label || '').trim();
    return {
      ...c, builtin: true,
      label: label || c.label, short: label || c.short, renamed: !!label,
      icon: o.icon || c.icon, color: o.color || c.color,
      order: typeof o.order === 'number' ? o.order : i * 10,
      restyled: overrides.has(c.id),
    };
  });
  const customs = customDocs.map(d => {
    const label = String(d.label || '').trim() || 'List';
    const task = d.kind === 'task';
    return {
      id: d.id, custom: true, renamed: true, label, short: label,
      icon: d.icon || '📋', color: d.color || FALLBACK_COLOR,
      kind: task ? 'task' : 'buy', sourceable: !task,
      noQty: d.qty === undefined ? task : !d.qty,
      order: typeof d.order === 'number' ? d.order : 1000,
      deleted: !!d.deleted, seeds: [],
    };
  });
  const all = [...builtins, ...customs].sort(byOrder);
  return { all, active: all.filter(l => !l.deleted), byId: new Map(all.map(l => [l.id, l])), routes, hidden };
}

/**
 * `route(name, fromId)` → the list id that name belongs in (see the header). Memoized
 * per (list, name) — catalogs run it over every history item on each render.
 * `route.byRule(name, fromId)` is the same answer ignoring hand-made moves.
 */
export function makeRouter(active, routes = {}) {
  const ids = new Set(active.map(l => l.id));
  const claimers = active.filter(l => (l.claimsFrom || []).length && CLAIM_RULES[l.id]);
  const byRule = (name, fromId) => {
    const c = claimers.find(l => l.id !== fromId && l.claimsFrom.includes(fromId) && CLAIM_RULES[l.id](name));
    return c ? c.id : fromId;
  };
  const memo = new Map();
  const route = (name, fromId) => {
    const key = routeKey(fromId, name);
    if (memo.has(key)) return memo.get(key);
    const moved = routes[key];
    const to = moved && ids.has(moved) ? moved : byRule(name, fromId);
    memo.set(key, to);
    return to;
  };
  route.byRule = byRule;
  return route;
}

/**
 * The route changes that make `names` live in `toId` from now on: every source list
 * whose notes of the name currently land in `fromId` now land in `toId`. An entry
 * that only restates the rule is removed instead of stored (keeps the doc small).
 * @returns [{ key, prev, next }] — `prev`/`next` undefined = no entry; feed `next` to
 *   the write and `prev` to its undo.
 */
export function planRoutes({ names, fromId, toId, sources, route, routes = {} }) {
  const out = [];
  const seen = new Set();
  (names || []).forEach(name => {
    (sources || []).forEach(src => {
      if (route(name, src) !== fromId) return;
      const key = routeKey(src, name);
      if (seen.has(key)) return;
      seen.add(key);
      const next = route.byRule(name, src) === toId ? undefined : toId;
      if (next !== routes[key]) out.push({ key, prev: routes[key], next });
    });
  });
  return out;
}

/**
 * The hidden-name entries that take `names` out of list `listId`'s suggestions, one per
 * name (any spelling counts once). `prev` = what was there (undefined = nothing) — the undo.
 * @returns [{ key, prev }]
 */
export function planHide({ names, listId, hidden = {} }) {
  const out = new Map();
  (names || []).forEach(name => {
    const key = routeKey(listId, name);
    // A stamp still pending on this device reads null — nothing to restore to.
    if (normalizeName(name) && !out.has(key)) out.set(key, { key, prev: hidden[key] == null ? undefined : hidden[key] });
  });
  return [...out.values()];
}

/** Everything components need about lists — built-ins only until the docs load. */
export function buildListsState(docs = [], { ready = true, error = '' } = {}) {
  const merged = mergeLists(docs);
  const route = makeRouter(merged.active, merged.routes);
  const catById = id => merged.byId.get(id) || unknownList(id);
  return { ...merged, route, catById, ready, error };
}

// Built-in task lists with their own verb strings (act.done.<id>); any other task list
// (a custom one) uses the generic "Done" / "Mark done".
const VERB_IDS = new Set(['prep', 'cooking', 'cleaning', 'issues']);
export const verbId = id => (VERB_IDS.has(id) ? id : 'task');
